import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import TipsAndUpdates from '@mui/icons-material/TipsAndUpdates';
import Email from '@mui/icons-material/Email';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Download from '@mui/icons-material/Download';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { toast } from 'react-toastify';
import { formatCurrency, formatPercentage } from '../../../utils/CurrencyFormat';
import {
  CapitalEfficiencySeedResponse,
  getCapitalEfficiencySeed,
  sendCapitalEfficiencyManualEmail,
} from '../../../services/request/Reports';
import { PageHeader } from '../../../shared-components/ui';

type Risk = 'Low' | 'Medium' | 'High';
type Effort = 'Low' | 'Medium' | 'High';

type AssetType = 'Lending' | 'Stocks' | 'Mutual Funds' | 'Business';

type AssetRow = {
  assetType: AssetType;
  capitalInvested: number;
  annualReturn: number;
  cePct: number;
  risk: Risk;
  effort: Effort;
};

function num(n: unknown): number {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return v;
}

function cePct(annualReturn: number, capital: number): number {
  if (capital <= 0) return 0;
  return (annualReturn / capital) * 100;
}

function riskScore(r: Risk): number {
  return r === 'Low' ? 1 : r === 'Medium' ? 2 : 3;
}

const PIE_COLORS = ['#1565c0', '#2e7d32', '#ef6c00', '#6a1b9a', '#00838f', '#5d4037'];

const CapitalEfficiencyDashboard: React.FC = () => {
  const [seed, setSeed] = useState<CapitalEfficiencySeedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Manual knobs (kept simple & interpretable)
  const [stocksDividends, setStocksDividends] = useState(0);
  const [mfDividends, setMfDividends] = useState(0);
  const [businessCapital, setBusinessCapital] = useState(0);
  const [businessAnnualProfit, setBusinessAnnualProfit] = useState(0);

  const [risk, setRisk] = useState<Record<AssetType, Risk>>({
    Lending: 'Medium',
    Stocks: 'High',
    'Mutual Funds': 'Medium',
    Business: 'High',
  });

  const [effort, setEffort] = useState<Record<AssetType, Effort>>({
    Lending: 'Medium',
    Stocks: 'Low',
    'Mutual Funds': 'Low',
    Business: 'High',
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getCapitalEfficiencySeed();
      if (res.data) {
        setSeed(res.data);
      } else {
        setSeed(null);
        setError('No data returned');
      }
    } catch (e: unknown) {
      setSeed(null);
      setError(e instanceof Error ? e.message : 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Manual calculator (one-time)
  const [manualFy, setManualFy] = useState(`FY ${new Date().getFullYear()}-${String(new Date().getFullYear() + 1).slice(-2)}`);
  const [manualCapital, setManualCapital] = useState(0);
  const [manualAnnualReturn, setManualAnnualReturn] = useState(0);
  const [manualNote, setManualNote] = useState('');
  const [manualRecipient, setManualRecipient] = useState('');
  const [sendingManual, setSendingManual] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  const lendingCapital = num(seed?.lendingCapital);
  const lendingAnnualReturn = num(seed?.lendingMonthlyInterestExpected) * 12;

  const stocksInvested = num(seed?.stocksInvested);
  const stocksCurrent = num(seed?.stocksCurrentValue);
  const stocksAnnualReturn = (stocksCurrent - stocksInvested) + num(stocksDividends);

  const mfInvested = num(seed?.mfInvested);
  const mfCurrent = num(seed?.mfCurrentValue);
  const mfAnnualReturn = (mfCurrent - mfInvested) + num(mfDividends);

  const businessInv = num(businessCapital);
  const businessAnnualReturn = num(businessAnnualProfit);

  const rows: AssetRow[] = useMemo(() => {
    const r: AssetRow[] = [
      {
        assetType: 'Lending',
        capitalInvested: lendingCapital,
        annualReturn: lendingAnnualReturn,
        cePct: cePct(lendingAnnualReturn, lendingCapital),
        risk: risk.Lending,
        effort: effort.Lending,
      },
      {
        assetType: 'Stocks',
        capitalInvested: stocksInvested,
        annualReturn: stocksAnnualReturn,
        cePct: cePct(stocksAnnualReturn, stocksInvested),
        risk: risk.Stocks,
        effort: effort.Stocks,
      },
      {
        assetType: 'Mutual Funds',
        capitalInvested: mfInvested,
        annualReturn: mfAnnualReturn,
        cePct: cePct(mfAnnualReturn, mfInvested),
        risk: risk['Mutual Funds'],
        effort: effort['Mutual Funds'],
      },
      {
        assetType: 'Business',
        capitalInvested: businessInv,
        annualReturn: businessAnnualReturn,
        cePct: cePct(businessAnnualReturn, businessInv),
        risk: risk.Business,
        effort: effort.Business,
      },
    ];

    // Hide rows that are completely empty (avoid noise)
    return r.filter((x) => x.capitalInvested !== 0 || x.annualReturn !== 0);
  }, [
    lendingCapital,
    lendingAnnualReturn,
    stocksInvested,
    stocksAnnualReturn,
    mfInvested,
    mfAnnualReturn,
    businessInv,
    businessAnnualReturn,
    risk,
    effort,
  ]);

  const portfolioCapital = useMemo(() => rows.reduce((s, r) => s + num(r.capitalInvested), 0), [rows]);
  const portfolioReturn = useMemo(() => rows.reduce((s, r) => s + num(r.annualReturn), 0), [rows]);
  const overallCe = cePct(portfolioReturn, portfolioCapital);

  const ranked = useMemo(() => {
    return [...rows].sort((a, b) => b.cePct - a.cePct);
  }, [rows]);

  const recommendation = useMemo(() => {
    if (ranked.length === 0) return null;
    // Simple ROIC-style “next ₹1” suggestion: highest CE, with tie-breaker lower risk.
    return [...ranked].sort((a, b) => {
      if (b.cePct !== a.cePct) return b.cePct - a.cePct;
      return riskScore(a.risk) - riskScore(b.risk);
    })[0];
  }, [ranked]);

  const insights = useMemo(() => {
    const out: string[] = [];
    if (ranked.length === 0) return out;

    const top = ranked[0];
    out.push(`Highest efficiency: ${top.assetType} (${formatPercentage(top.cePct)}).`);

    const low = ranked.filter((r) => r.cePct > 0 && r.cePct < 10);
    if (low.length > 0) {
      out.push(`Low efficiency capital detected in ${low.map((x) => `${x.assetType} (${formatPercentage(x.cePct)})`).join(', ')}.`);
    }

    if (recommendation) {
      out.push(`Recommendation (next ₹1): Prefer ${recommendation.assetType} based on CE% (tie-break: lower risk).`);
    }

    // Overexposure flag: >50% capital in a High risk bucket
    const hi = rows.filter((r) => r.risk === 'High' && portfolioCapital > 0 && (r.capitalInvested / portfolioCapital) * 100 > 50);
    if (hi.length > 0) {
      out.push(`Overexposure risk: >50% capital in high-risk asset (${hi[0].assetType}).`);
    }
    return out;
  }, [ranked, recommendation, rows, portfolioCapital]);

  const pieRows = useMemo(() => {
    return rows
      .filter((r) => r.capitalInvested > 0)
      .map((r) => ({ name: r.assetType, value: r.capitalInvested }));
  }, [rows]);

  const barRows = useMemo(() => {
    return ranked.map((r) => ({ name: r.assetType, ce: Number(r.cePct.toFixed(2)) }));
  }, [ranked]);

  const manualCe = cePct(manualAnnualReturn, manualCapital);
  const manualPayback = manualAnnualReturn > 0 ? manualCapital / manualAnnualReturn : 0;

  const manualReportText = useMemo(() => {
    const lines: string[] = [];
    lines.push('Capital Efficiency — Manual one-time report');
    lines.push(`FY: ${manualFy}`);
    lines.push(`Capital invested: ${formatCurrency(manualCapital, 'INR', true)}`);
    lines.push(`Annual return: ${formatCurrency(manualAnnualReturn, 'INR', true)}`);
    lines.push(`CE%: ${formatPercentage(manualCe)}`);
    lines.push(`Payback period: ${manualPayback > 0 ? `${manualPayback.toFixed(2)} years` : '—'}`);
    if (manualNote.trim()) {
      lines.push(`Note: ${manualNote.trim()}`);
    }
    lines.push(`Generated at: ${new Date().toISOString()}`);
    return lines.join('\n');
  }, [manualFy, manualCapital, manualAnnualReturn, manualCe, manualPayback, manualNote]);

  const downloadManualReport = useCallback(() => {
    const blob = new Blob([manualReportText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `capital-efficiency-manual-${manualFy.replace(/[^0-9A-Za-z-]+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Manual report downloaded.');
  }, [manualReportText, manualFy]);

  const copyManualReport = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(manualReportText);
      toast.success('Manual report copied.');
    } catch {
      toast.error('Copy failed (clipboard permission).');
    }
  }, [manualReportText]);

  const emailManualReport = useCallback(async () => {
    try {
      setSendingManual(true);
      await sendCapitalEfficiencyManualEmail({
        recipientEmail: manualRecipient.trim() || undefined,
        fyYear: manualFy,
        capitalInvested: manualCapital,
        annualReturn: manualAnnualReturn,
        note: manualNote.trim() || undefined,
      });
      toast.success('Manual report email triggered.');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to send email.');
    } finally {
      setSendingManual(false);
    }
  }, [manualRecipient, manualFy, manualCapital, manualAnnualReturn, manualNote]);

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Reports"
        title="Capital efficiency"
        subtitle="CE% = (Annual Net Return / Total Capital Invested) × 100 across asset classes."
        actions={
          <Button
            variant="contained"
            startIcon={<Refresh />}
            disabled={loading}
            onClick={() => {
              void load();
              toast.info('Refreshing seed data…');
            }}
          >
            Refresh
          </Button>
        }
      />

      {loading && <CircularProgress size={32} sx={{ display: 'block', mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {seed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          As of {seed.asOfDate}. {seed.message}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ columnGap: 2, rowGap: 1 }}>
            <Chip label={`Total capital: ${formatCurrency(portfolioCapital, 'INR', true)}`} color="primary" variant="outlined" />
            <Chip label={`Total annual return: ${formatCurrency(portfolioReturn, 'INR', true)}`} color="success" variant="outlined" />
            <Chip label={`Overall CE: ${formatPercentage(overallCe)}`} color="secondary" variant="outlined" />
          </Stack>
          {portfolioReturn > 0 && portfolioCapital > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Overall payback period (portfolio): {(portfolioCapital / portfolioReturn).toFixed(2)} years.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Manual inputs (for accuracy)
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              label="Stocks dividends (annual)"
              type="number"
              value={stocksDividends}
              onChange={(e) => setStocksDividends(num(e.target.value))}
              sx={{ minWidth: 260 }}
            />
            <TextField
              label="MF dividends (annual)"
              type="number"
              value={mfDividends}
              onChange={(e) => setMfDividends(num(e.target.value))}
              sx={{ minWidth: 260 }}
            />
            <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
            <TextField
              label="Business capital invested"
              type="number"
              value={businessCapital}
              onChange={(e) => setBusinessCapital(num(e.target.value))}
              sx={{ minWidth: 260 }}
            />
            <TextField
              label="Business annual profit"
              type="number"
              value={businessAnnualProfit}
              onChange={(e) => setBusinessAnnualProfit(num(e.target.value))}
              sx={{ minWidth: 260 }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Manual calculator (one-time report)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter invested capital and annual return for a financial year. This generates a one-time CE report you can copy, download, or email.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <TextField
              label="FY year"
              value={manualFy}
              onChange={(e) => setManualFy(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <TextField
              label="Invested amount"
              type="number"
              value={manualCapital}
              onChange={(e) => setManualCapital(num(e.target.value))}
              sx={{ minWidth: 240 }}
            />
            <TextField
              label="Annual return / revenue"
              type="number"
              value={manualAnnualReturn}
              onChange={(e) => setManualAnnualReturn(num(e.target.value))}
              sx={{ minWidth: 260 }}
            />
            <TextField
              label="Email recipient (optional)"
              value={manualRecipient}
              onChange={(e) => setManualRecipient(e.target.value)}
              sx={{ minWidth: 280 }}
              placeholder="Leave blank to use default report email"
            />
          </Stack>

          <TextField
            label="Note (optional)"
            value={manualNote}
            onChange={(e) => setManualNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip label={`CE: ${formatPercentage(manualCe)}`} color="secondary" variant="outlined" />
            <Chip
              label={`Payback: ${manualPayback > 0 ? `${manualPayback.toFixed(2)} yrs` : '—'}`}
              color="primary"
              variant="outlined"
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<ContentCopy />} onClick={() => void copyManualReport()}>
              Copy
            </Button>
            <Button variant="outlined" startIcon={<Download />} onClick={downloadManualReport}>
              Download
            </Button>
            <Button
              variant="contained"
              startIcon={<Email />}
              onClick={() => void emailManualReport()}
              disabled={sendingManual || !manualFy.trim()}
            >
              {sendingManual ? 'Sending…' : 'Send email'}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, whiteSpace: 'pre-wrap' }}>
            {manualReportText}
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Asset-wise breakdown (ranked by CE%)
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset Type</TableCell>
                  <TableCell align="right">Capital Invested</TableCell>
                  <TableCell align="right">Annual Return</TableCell>
                  <TableCell align="right">CE %</TableCell>
                  <TableCell>Risk</TableCell>
                  <TableCell>Effort</TableCell>
                  <TableCell align="right">Payback (yrs)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ranked.map((r, idx) => {
                  const payback = r.annualReturn > 0 ? r.capitalInvested / r.annualReturn : 0;
                  return (
                    <TableRow key={r.assetType} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={`#${idx + 1}`} variant="outlined" />
                          <Typography variant="body2" fontWeight={600}>
                            {r.assetType}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(r.capitalInvested, 'INR', true)}</TableCell>
                      <TableCell align="right">{formatCurrency(r.annualReturn, 'INR', true)}</TableCell>
                      <TableCell align="right">{formatPercentage(r.cePct)}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Risk</InputLabel>
                          <Select
                            label="Risk"
                            value={risk[r.assetType]}
                            onChange={(e) => setRisk((prev) => ({ ...prev, [r.assetType]: e.target.value as Risk }))}
                          >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Effort</InputLabel>
                          <Select
                            label="Effort"
                            value={effort[r.assetType]}
                            onChange={(e) => setEffort((prev) => ({ ...prev, [r.assetType]: e.target.value as Effort }))}
                          >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">{payback > 0 ? payback.toFixed(2) : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TipsAndUpdates color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Insights
            </Typography>
          </Stack>
          {insights.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No insights yet (add data via Breeze capture / net worth snapshots / manual inputs).
            </Typography>
          ) : (
            <Stack spacing={1}>
              {insights.map((s, i) => (
                <Alert key={i} severity="success" variant="outlined">
                  {s}
                </Alert>
              ))}
            </Stack>
          )}
          {recommendation && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              ROIC-style suggestion: deploy incremental capital into the asset with highest CE% (with a lower-risk tie-breaker).
            </Typography>
          )}
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1, minHeight: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Capital allocation
            </Typography>
            {pieRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No capital allocation data.
              </Typography>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieRows} dataKey="value" nameKey="name" outerRadius={95} label>
                      {pieRows.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip formatter={(v: any) => formatCurrency(Number(v), 'INR', true)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, minHeight: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              CE % by asset
            </Typography>
            {barRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No CE data.
              </Typography>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ReTooltip formatter={(v: any) => `${Number(v).toFixed(2)}%`} />
                    <Bar dataKey="ce" fill="#3949ab" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Note: Keep this interpretable. If you want XIRR / time-weighted returns later, we can add it once we standardize cashflows.
      </Typography>
    </Box>
  );
};

export default CapitalEfficiencyDashboard;

